import { Check, X } from 'lucide-react'
import React, { useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import { Autocomplete, TextInput } from 'slab'
import { useGetEmployersEmployees } from 'utils/web_api'
import { type EmployersEmployee } from 'utils/web_api.schemas'

import { StatusBadge } from './StatusBadge'
import { type MergeState } from './types'

interface EmployeeSearchCellProps {
  duplicateEmployee: EmployersEmployee
  mergeStates: MergeState
  setMergeStates: React.Dispatch<React.SetStateAction<MergeState>>
}

export const EmployeeSearchCell = ({
  duplicateEmployee,
  mergeStates,
  setMergeStates
}: EmployeeSearchCellProps) => {
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearchValue] = useDebounceValue(searchValue, 300)

  const mergeState = mergeStates[duplicateEmployee.id]
  const selectedEmployee = mergeState?.selectedEmployee

  // Fetch employees for autocomplete using the generated hook
  // Always fetch employees - either with search or pre-load 20 payroll-synced employees
  const { data: employeesData, isLoading: searchLoading } = useGetEmployersEmployees(
    {
      ...(debouncedSearchValue.trim() && { search: debouncedSearchValue }),
      payroll_synced: true,
      'page[size]': 20,
    }
  )

  const searchResults = employeesData?.data || []

  const handleEmployeeSelect = (employee: EmployersEmployee) => {
    setMergeStates(prev => ({
      ...prev,
      [duplicateEmployee.id]: {
        ...prev[duplicateEmployee.id],
        selectedEmployee: employee,
      },
    }))
    setSearchValue('')
  }

  const handleRemoveSelection = () => {
    setMergeStates(prev => ({
      ...prev,
      [duplicateEmployee.id]: {
        ...prev[duplicateEmployee.id],
        selectedEmployee: null,
      },
    }))
  }

  if (selectedEmployee) {
    const isMerged = mergeState?.isMerged || false
    const mergedEmployee = mergeState?.mergedEmployee

    // If merged, show the canonical employee data
    const displayEmployee = isMerged && mergedEmployee ? mergedEmployee : selectedEmployee

    return (
      <div className='flex items-center justify-between p-2 border border-gray-200 rounded-md bg-white'>
        <div className='flex flex-col gap-y-1'>
          <div className='flex items-center gap-2'>
            <span className='text-md font-medium'>
              {displayEmployee.first_name} {displayEmployee.last_name}
            </span>
            <StatusBadge employee={displayEmployee} />
          </div>
          <span className='text-xs text-gray-500'>{displayEmployee.work_email}</span>
        </div>
        {isMerged
          ? (
            <Check className='text-green-600' size={16} />
            )
          : (
            <button
              onClick={handleRemoveSelection}
              className='p-1 hover:bg-gray-200 rounded-full ml-2 flex-shrink-0'
            >
              <X size={16} />
            </button>
            )}
      </div>
    )
  }

  return (
    <div className='w-full'>
      <Autocomplete>
        <Autocomplete.Input asChild>
          <TextInput
            placeholder='Search by name or email'
            value={searchValue}
            onChange={({ target }) => setSearchValue(target.value)}
          />
        </Autocomplete.Input>
        <Autocomplete.List>
          {searchLoading && !searchResults.length && (
            <div className='px-4 py-3 text-xs text-gray-500'>
              Loading...
            </div>
          )}
          {!searchLoading && searchResults.length === 0 && searchValue.trim() && (
            <div className='px-4 py-3 text-xs text-gray-500'>
              No results
            </div>
          )}
          {searchResults
            .filter(employee => employee.id !== duplicateEmployee.id)
            .map((employee) => (
              <Autocomplete.Option
                key={employee.id}
                value={`${employee.first_name} ${employee.last_name}`}
                onClick={() => handleEmployeeSelect(employee)}
              >
                <div className='flex flex-col gap-y-1 p-2'>
                  <div className='flex items-center gap-2'>
                    <span className='text-md font-medium'>
                      {employee.first_name} {employee.last_name}
                    </span>
                    <StatusBadge employee={employee} />
                  </div>
                  <span className='text-xs text-gray-500'>{employee.work_email}</span>
                </div>
              </Autocomplete.Option>
            ))}
          {!searchLoading && !debouncedSearchValue.trim() && searchResults.length > 0 && (
            <div className='px-4 py-3 text-xs text-gray-500 italic border-t border-gray-100'>
              Search to see more employees
            </div>
          )}
        </Autocomplete.List>
      </Autocomplete>
    </div>
  )
}
